import type { ABTestValue } from './sdk';

// --- Volcano SDK types ---

interface VolcanoCollectEvent {
  (...args: unknown[]): unknown;
  l?: number;
  q?: unknown[][];
}

interface VolcanoAllVarsItem {
  val?: ABTestValue;
  value?: ABTestValue;
  vid?: string;
}

declare global {
  interface Window {
    ABCollectEvent?: VolcanoCollectEvent;
    LogAnalyticsObject?: string;
  }
}

const VOLCANO_GLOBAL_NAME = 'ABCollectEvent';
const VOLCANO_SDK_URL = 'https://cdn.wegic.ai/assets/js/ABTestSDK-v5.1.10.js';
const VOLCANO_SCRIPT_FILENAME = 'ABTestSDK-v5.1.10.js';

export interface VolcanoSDKOptions {
  /** 单次回调等待超时（毫秒） */
  timeout: number;
}

export interface VolcanoInitConfig {
  appId: number;
  channel: string;
  userId?: string;
}

export interface VolcanoSnapshot {
  experiments: Record<string, ABTestValue>;
  vids: Record<string, string>;
  abVersion: string;
}

/**
 * 火山引擎 A/B 实验 SDK 的薄封装：
 * - 负责脚本注入、命令队列占位、初始化、用户配置
 * - 把 SDK 的回调风格统一封装为 Promise，调用方只用 `await`
 */
export class VolcanoSDK {
  private readonly timeout: number;

  constructor(options: VolcanoSDKOptions) {
    this.timeout = options.timeout;
  }

  /** 注入脚本并下发 init / config，等待 SDK 就绪 */
  async init(config: VolcanoInitConfig): Promise<void> {
    this.ensureQueue();
    this.injectScript();

    this.send('init', {
      app_id: config.appId,
      channel: config.channel,
      enable_ab_test: true,
    });

    if (config.userId) {
      this.send('config', { user_unique_id: config.userId });
    }

    await this.waitReady();
  }

  /** 拉取所有实验值（不触发曝光，曝光由调用方按需 `expose` 触发） */
  async getAllVars(): Promise<VolcanoSnapshot> {
    const allVars = await this.callWithCallback<Record<string, VolcanoAllVarsItem> | null | undefined>(
      (cb) => {
 this.send('getAllVars', cb);
},
    );

    if (!allVars) {
      return { experiments: {}, vids: {}, abVersion: this.getAbVersion() };
    }

    const experiments: Record<string, ABTestValue> = {};
    const vids: Record<string, string> = {};

    for (const [key, item] of Object.entries(allVars)) {
      experiments[key] = item?.val ?? item?.value ?? null;
      if (typeof item?.vid === 'string' && item.vid) {
        vids[key] = item.vid;
      }
    }

    return { experiments, vids, abVersion: this.getAbVersion() };
  }

  /** 触发火山 SDK 自身的曝光上报。底层仍走 `getVar`，回调留空即可。 */
  expose(name: string): void {
    try {
      this.send('getVar', name, '', () => {});
    } catch {}
  }

  // --- Private ---

  /** 同步调用，读取 ab sdk 版本号 */
  private getAbVersion(): string {
    try {
      const v = this.send('getAbSdkVersion');
      return typeof v === 'string' ? v : '';
    } catch {
      return '';
    }
  }

  /** start + getToken 走通则视为就绪 */
  private waitReady(): Promise<void> {
    return this.callWithCallback<void>((cb) => {
      this.send('start');
      this.send('getToken', cb);
    });
  }

  /** 把火山「最后一个参数为回调」的调用统一包装为 Promise，带超时与异常兜底 */
  private callWithCallback<T>(invoke: (callback: (value: T) => void) => void): Promise<T> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (value: T) => {
        if (settled) {
 return;
}
        settled = true;
        resolve(value);
      };

      const timer = window.setTimeout(() => finish(undefined as T), this.timeout);

      try {
        invoke((value: T) => {
          window.clearTimeout(timer);
          finish(value);
        });
      } catch {
        window.clearTimeout(timer);
        finish(undefined as T);
      }
    });
  }

  private send(...args: unknown[]): unknown {
    const collect = this.getCollectEvent();
    if (!collect) {
      throw new Error('Volcano SDK not found');
    }
    return collect(...args);
  }

  private getCollectEvent(): VolcanoCollectEvent | null {
    const candidate = (window as unknown as Record<string, VolcanoCollectEvent | undefined>)[VOLCANO_GLOBAL_NAME];
    return typeof candidate === 'function' ? candidate : null;
  }

  /** 在脚本就绪前先占位，把调用塞进队列，等脚本加载后回放 */
  private ensureQueue(): void {
    if (this.getCollectEvent()) {
      return;
    }
    const stub = function (...args: unknown[]) {
      (stub.q = stub.q || []).push(args);
    } as VolcanoCollectEvent;
    stub.l = Date.now();
    window[VOLCANO_GLOBAL_NAME] = stub;
    window.LogAnalyticsObject = VOLCANO_GLOBAL_NAME;
  }

  private injectScript(): void {
    if (typeof document === 'undefined') {
      return;
    }
    if (document.querySelector?.(`script[src*="${VOLCANO_SCRIPT_FILENAME}"]`)) {
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = VOLCANO_SDK_URL;
    document.head.appendChild(script);
  }
}
